import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import QRCode from "react-qr-code";
import moment from "moment";
import { DadosPixSucesso, IRetornoEmitirPix } from "../../../../types/mutations/financeiro/emitir-pix";
import { formatarValorMonetario } from "../../../../helpers/masks";
import axiosClient from "../../../../lib/axios";
import PaperDraggable from "../../../PaperDraggable";
import LabelPrinter from "../../../LabelPrinter";
import ChipComCopyIcon from "../../../ChipComCopyIcon";
import BtnCompartilhar from "../../../BtnCompartilhar";
import LoadingApiQuery from "../../../LoadingApiQuery";
import DialogPixSucesso from "../DialogPixSucesso";
import AguardandoPagamento from "./AguardandoPagamento";
import TemporizadorPix from "./TemporizadorPix";

interface DialogPixProps {
  open: boolean;
  routes: IRoutesEmitirPix;
  mensagem?: string;
  tipo_publico?: string;
  codigo_publico?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export interface IRoutesEmitirPix {
  route: string;
  routeParams: Record<string, any>;
  aguardandoPagamentoRoute: string;
  aguardandoPagamentoCodigo?: string | number;
}

const LOCAL_STORAGE_KEYS = {
  PIX_BRCODE: 'lastPixBrcode',
  PIX_START_TIME: 'pixStartTime'
};

export default function DialogPix({
  open,
  routes,
  mensagem,
  tipo_publico,
  codigo_publico,
  onClose,
  onSuccess
}: DialogPixProps) {
  const [dadosPix, setDadosPix] = useState<IRetornoEmitirPix | null>(null);
  const [pagamentoRealizado, setPagamentoRealizado] = useState(false);
  const [dadosPixSucesso, setDadosPixSucesso] = useState<DadosPixSucesso | null>(null);

  const { mutate: emitirPix, isLoading } = useMutation({
    mutationKey: "emitir-pix",
    mutationFn: () => {
      return axiosClient.post(routes.route, {
        ...routes.routeParams,
      });
    },
    onSuccess: ({ data }) => {
      setDadosPix(data);
      const dataStorage = {
        valor: data.valor,
        brcode: data.brcode,
        expiracao_pix: data.expiracao_pix,
        codigo: data.codigo
      };
      localStorage.setItem('lastPixBrcode', JSON.stringify(dataStorage));
    },
  });

  const clearPixData = () => {
    Object.values(LOCAL_STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  };


  const handlePixPago = (data: DadosPixSucesso) => {
    clearPixData();
    setPagamentoRealizado(true);
    setDadosPixSucesso(data);
  };

  const handlePixExpired = () => {
    clearPixData();
    setDadosPix(null);
    emitirPix();
  };

  useEffect(() => {
    if (!open) {
      setDadosPix(null);
      return;
    }

    const savedPixData = localStorage.getItem(LOCAL_STORAGE_KEYS.PIX_BRCODE);
    savedPixData ? setDadosPix(JSON.parse(savedPixData)) : emitirPix();
  }, [open, emitirPix]);

  useEffect(() => {
    return () => {
      if (pagamentoRealizado) {
        clearPixData();
      }
    };
  }, [pagamentoRealizado, clearPixData]);

  if (pagamentoRealizado && dadosPixSucesso) {
    return <DialogPixSucesso data={dadosPixSucesso} open={open} onClose={onSuccess || onClose} />;
  }

  return (
    <>
      {pagamentoRealizado && dadosPixSucesso ? (
        <DialogPixSucesso
          data={dadosPixSucesso}
          open={open}
          onClose={onSuccess ? onSuccess : onClose}
        />
      ) : (
        <Dialog
          open={open}
          onClose={onClose}
          PaperComponent={PaperDraggable}
          aria-labelledby="draggable-dialog-title"
        >
          <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
            <b>PIX instantâneo</b>
          </DialogTitle>
          <DialogContent>
            <Typography>
              (Um QRCode é gerado para pagamento em até 5 minutos)
            </Typography>
            {isLoading && (
              <LoadingApiQuery extraParams={{ alignItems: "center" }} />
            )}
            {dadosPix && (
              <>
                <Grid container size={{ xs: 12 }} mt={2}>
                  <Box mb={2} width={168} height={168}>
                    <QRCode
                      size={168}
                      style={{
                        height: "auto",
                        maxWidth: "100%",
                        width: "100%",
                      }}
                      value={dadosPix.brcode}
                    />
                  </Box>
                  <Box ml={2} display={"flex"} flexDirection={"column"}>
                    <LabelPrinter
                      titulo="Valor"
                      size="small"
                      valor={formatarValorMonetario(dadosPix.valor)}
                    />
                    <Box mt={2} mb={2}>
                      <Typography variant="subtitle2">Gerado em</Typography>
                      <Typography variant="body1" fontWeight={"bold"}>
                        {moment(dadosPix.data_criacao).format("DD/MM/YYYY")}
                      </Typography>
                    </Box>
                    <AguardandoPagamento
                      route={routes.aguardandoPagamentoRoute}
                      routeCode={routes.aguardandoPagamentoCodigo ? routes.aguardandoPagamentoCodigo : dadosPix.codigo}
                      onPixPago={handlePixPago}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box mb={2}>
                    <Typography variant="subtitle2">Código PIX</Typography>
                    <ChipComCopyIcon
                      title="Código de barras"
                      codigo={dadosPix.brcode}
                    />
                  </Box>
                  <TemporizadorPix
                    expiracao={dadosPix.expiracao_pix}
                    onExpired={handlePixExpired}
                  />
                </Grid>

                <Grid size={{ xs: 12 }} gap={8} mt={2}>
                  <Box>
                    <BtnCompartilhar
                      {...{ tipo_publico, codigo_publico }}
                      mensagem={mensagem ? `${mensagem} ${dadosPix.brcode}` : `Segue abaixo o código PIX no valor de ${formatarValorMonetario(
                        dadosPix.valor
                      )} para o pagamento do(s) título(s):\n${dadosPix.brcode}`}
                    />
                  </Box>
                </Grid>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="primary">
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
